// src/services/zusatzleistungen/zusatzleistungenService.ts

/**
 * Service für Zusatzleistungen (zusätzlich abzurechnende Leistungen) und den
 * kundenbezogenen Stundensatz.
 *
 * Verantwortlich für:
 * - Stundensatz je Kunde (zeitlich gültig, analog customer_pricing)
 * - CRUD auf Zusatzleistungen (jederzeit erfassbar, unabhängig vom Entwurf)
 * - Ermittlung offener Zusatzleistungen im Scope einer Rechnung (inkl.
 *   Sammelrechnung: Unter-Kunden eines Auftraggebers zählen mit)
 * - Übernahme einer offenen Zusatzleistung als Rechnungsposition
 *
 * Die Betragsneuberechnung der Rechnung erfolgt bewusst über den bestehenden
 * invoiceService.recalculateInvoiceTotals – es wird keine Rechenlogik dupliziert.
 */

import { supabase } from '../../lib/supabaseClient';
import { invoiceService } from '../invoice/invoiceService';
import {
  Zusatzleistung,
  CustomerStundensatz,
  CreateZusatzleistungInput,
  UpdateZusatzleistungInput,
  ZusatzleistungFilter,
  Invoice
} from '../../features/invoices/types';

export const zusatzleistungenService = {

  // ============================================
  // STUNDENSATZ (je Kunde, zeitlich gültig)
  // ============================================

  /**
   * Lädt den heute gültigen Stundensatz für einen Kunden.
   * Entspricht invoiceService.getCurrentPricing ohne Abrechnungsmonat; eine
   * Suche nach dem Abrechnungsmonat gibt es für den Stundensatz nicht.
   *
   * Bei "im Auftrag von"-Kunden (Unterkunde mit gesetztem auftraggeber_id) gilt
   * der Stundensatz des Auftraggebers – Zusatzleistungen solcher Kunden werden
   * auf der Rechnung des Auftraggebers berechnet, also zählt auch dessen Satz.
   * Bewusst getrennte Query statt PostgREST-Self-Embed (Hausregel).
   */
  getCurrentStundensatz: async (customerId: string): Promise<CustomerStundensatz | null> => {
    const today = new Date().toISOString().split('T')[0];

    // Maßgeblichen Kunden bestimmen: Unterkunde -> Auftraggeber.
    let effectiveCustomerId = customerId;
    const { data: customerRow } = await supabase
      .from('customers')
      .select('auftraggeber_id')
      .eq('id', customerId)
      .maybeSingle();
    if (customerRow?.auftraggeber_id) {
      effectiveCustomerId = customerRow.auftraggeber_id as string;
    }

    const { data, error } = await supabase
      .from('customer_stundensatz')
      .select('*')
      .eq('customer_id', effectiveCustomerId)
      .lte('gueltig_ab', today)
      .or(`gueltig_bis.is.null,gueltig_bis.gte.${today}`)
      .order('gueltig_ab', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Laden des Stundensatzes:', error);
      return null;
    }
    return data as CustomerStundensatz | null;
  },

  /**
   * Speichert oder aktualisiert den Stundensatz für einen Kunden.
   *
   * Prüft zuerst, ob für heute bereits ein Eintrag existiert (Update),
   * ansonsten wird der bisher offene Eintrag zum Vortag abgeschlossen und ein
   * neuer Eintrag ab heute angelegt. Anders als invoiceService.savePricing
   * schützt diese Funktion keine künftigen Einträge (gueltig_ab nach heute):
   * Es werden alle offenen Einträge abgeschlossen.
   */
  saveStundensatz: async (customerId: string, stundensatz: number): Promise<boolean> => {
    const today = new Date().toISOString().split('T')[0];

    try {
      const { data: existing } = await supabase
        .from('customer_stundensatz')
        .select('id')
        .eq('customer_id', customerId)
        .eq('gueltig_ab', today)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('customer_stundensatz')
          .update({
            stundensatz,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) {
          console.error('[ZusatzleistungenService] Fehler beim Aktualisieren des Stundensatzes:', error);
          return false;
        }
      } else {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];

        await supabase
          .from('customer_stundensatz')
          .update({ gueltig_bis: yesterdayStr })
          .eq('customer_id', customerId)
          .is('gueltig_bis', null);

        const { error } = await supabase
          .from('customer_stundensatz')
          .insert({
            customer_id: customerId,
            stundensatz,
            gueltig_ab: today
          });

        if (error) {
          console.error('[ZusatzleistungenService] Fehler beim Speichern des Stundensatzes:', error);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('[ZusatzleistungenService] Unerwarteter Fehler beim Speichern des Stundensatzes:', error);
      return false;
    }
  },

  // ============================================
  // ZUSATZLEISTUNGEN - CRUD
  // ============================================

  /**
   * Lädt Zusatzleistungen mit optionalem Filter (Kunde/Jahr/Monat/Status).
   * Sortiert nach Leistungsmonat absteigend, dann Erfassungszeitpunkt.
   *
   * Für "im Auftrag von"-Kunden wird zusätzlich der Firmenname des Auftraggebers
   * nachgeladen (Sichtbarkeit "berechnet an" in der Liste). Bewusst getrennte
   * Queries statt PostgREST-Self-Embed (Hausregel).
   */
  list: async (filter?: ZusatzleistungFilter): Promise<Zusatzleistung[]> => {
    let query = supabase
      .from('zusatzleistungen')
      .select(`
        *,
        customer:customers(company, mandantennummer)
      `)
      .order('year', { ascending: false })
      .order('month', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(100000);

    if (filter?.customerId) query = query.eq('customer_id', filter.customerId);
    if (filter?.year) query = query.eq('year', filter.year);
    if (filter?.month) query = query.eq('month', filter.month);
    if (filter?.status) query = query.eq('status', filter.status);

    const { data, error } = await query;

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Laden der Zusatzleistungen:', error);
      return [];
    }

    const items = (data || []) as Zusatzleistung[];

    // Auftraggeber-Namen für Unterkunden nachladen (Anzeige "berechnet an").
    const customerIds = Array.from(new Set(items.map(i => i.customer_id)));
    if (customerIds.length > 0) {
      const { data: custRows } = await supabase
        .from('customers')
        .select('id, auftraggeber_id')
        .in('id', customerIds)
        .limit(100000);

      const auftraggeberByCustomer: Record<string, string> = {};
      for (const row of custRows || []) {
        if (row.auftraggeber_id) auftraggeberByCustomer[row.id] = row.auftraggeber_id as string;
      }

      const auftraggeberIds = Array.from(new Set(Object.values(auftraggeberByCustomer)));
      if (auftraggeberIds.length > 0) {
        const { data: agRows } = await supabase
          .from('customers')
          .select('id, company, company_rechnung')
          .in('id', auftraggeberIds)
          .limit(100000);

        const companyById: Record<string, string> = {};
        for (const row of agRows || []) {
          companyById[row.id] = (row.company_rechnung || row.company) as string;
        }

        for (const item of items) {
          const agId = auftraggeberByCustomer[item.customer_id];
          if (agId && companyById[agId]) {
            item.auftraggeber_company = companyById[agId];
          }
        }
      }
    }

    return items;
  },

  /**
   * Legt eine neue Zusatzleistung an (immer Status 'offen').
   *
   * Der Betrag wird so gespeichert wie übergeben – bei 'stunden' berechnet der
   * Aufrufer i.d.R. stunden*stundensatz vor, bei 'festbetrag' ist es der freie
   * Betrag (auch negativ für Gutschriften zulässig).
   */
  create: async (input: CreateZusatzleistungInput): Promise<Zusatzleistung | null> => {
    const { data, error } = await supabase
      .from('zusatzleistungen')
      .insert({
        customer_id: input.customerId,
        year: input.year,
        month: input.month,
        art: input.art,
        stunden: input.art === 'stunden' ? (input.stunden ?? null) : null,
        stundensatz: input.art === 'stunden' ? (input.stundensatz ?? null) : null,
        betrag: input.betrag,
        beschreibung: input.beschreibung,
        notiz: input.notiz ?? null,
        status: 'offen',
        erfasst_durch_staff_id: input.erfasstDurchStaffId ?? null
      })
      .select()
      .single();

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Anlegen der Zusatzleistung:', error);
      return null;
    }
    return data as Zusatzleistung;
  },

  /**
   * Aktualisiert eine Zusatzleistung.
   *
   * Sicherheits-Guard: Nur Einträge mit Status 'offen' dürfen geändert werden.
   * Bereits in eine Rechnung übernommene Einträge ('in_rechnung') bleiben
   * unverändert, damit die auf der Rechnung stehende Position nicht still von
   * ihrer Quelle abweicht.
   */
  update: async (id: string, updates: UpdateZusatzleistungInput): Promise<{ success: boolean; error?: string }> => {
    const { data: current, error: loadError } = await supabase
      .from('zusatzleistungen')
      .select('status')
      .eq('id', id)
      .single();

    if (loadError || !current) {
      return { success: false, error: 'Zusatzleistung nicht gefunden' };
    }
    if (current.status !== 'offen') {
      return { success: false, error: 'Nur offene Zusatzleistungen können geändert werden. Dieser Eintrag ist bereits in einer Rechnung.' };
    }

    // Bei 'festbetrag' Stunden/Satz konsequent leeren; bei 'stunden' durchreichen.
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.art !== undefined) payload.art = updates.art;
    if (updates.beschreibung !== undefined) payload.beschreibung = updates.beschreibung;
    if (updates.notiz !== undefined) payload.notiz = updates.notiz;
    if (updates.betrag !== undefined) payload.betrag = updates.betrag;
    if (updates.year !== undefined) payload.year = updates.year;
    if (updates.month !== undefined) payload.month = updates.month;

    const effektiveArt = updates.art;
    if (effektiveArt === 'festbetrag') {
      payload.stunden = null;
      payload.stundensatz = null;
    } else {
      if (updates.stunden !== undefined) payload.stunden = updates.stunden;
      if (updates.stundensatz !== undefined) payload.stundensatz = updates.stundensatz;
    }

    const { error } = await supabase
      .from('zusatzleistungen')
      .update(payload)
      .eq('id', id)
      .eq('status', 'offen'); // Doppelter Guard auf DB-Ebene

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Aktualisieren der Zusatzleistung:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  /**
   * Löscht eine Zusatzleistung.
   *
   * Sicherheits-Guard: Nur Einträge mit Status 'offen' dürfen gelöscht werden.
   */
  remove: async (id: string): Promise<{ success: boolean; error?: string }> => {
    const { data: current, error: loadError } = await supabase
      .from('zusatzleistungen')
      .select('status')
      .eq('id', id)
      .single();

    if (loadError || !current) {
      return { success: false, error: 'Zusatzleistung nicht gefunden' };
    }
    if (current.status !== 'offen') {
      return { success: false, error: 'Nur offene Zusatzleistungen können gelöscht werden. Dieser Eintrag ist bereits in einer Rechnung.' };
    }

    const { error } = await supabase
      .from('zusatzleistungen')
      .delete()
      .eq('id', id)
      .eq('status', 'offen'); // Doppelter Guard auf DB-Ebene

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Löschen der Zusatzleistung:', error);
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  // ============================================
  // SCOPE-ERMITTLUNG (offene Leistungen zu einer Rechnung)
  // ============================================

  /**
   * Ermittelt die customer_ids, die zum Abrechnungs-Scope eines Rechnungs-
   * Empfängers gehören: der Empfänger selbst plus – falls er Auftraggeber einer
   * Sammelrechnung ist – alle seine (nicht gelöschten) Unter-Kunden.
   *
   * Bewusst getrennte Query statt PostgREST-Embed (Hausregel).
   */
  getScopeCustomerIds: async (customerId: string): Promise<string[]> => {
    const { data: subs, error } = await supabase
      .from('customers')
      .select('id')
      .eq('auftraggeber_id', customerId)
      .eq('deleted', false)
      .limit(100000);

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Laden der Unter-Kunden:', error);
      return [customerId];
    }

    const ids = [customerId, ...((subs || []).map(s => s.id))];
    // Duplikate entfernen (falls ein Kunde sich selbst referenzieren würde)
    return Array.from(new Set(ids));
  },

  /**
   * Lädt alle offenen Zusatzleistungen im Scope einer Rechnung (Empfänger +
   * ggf. Unter-Kunden) – bewusst UNABHÄNGIG vom Leistungsmonat. Sonderposten
   * sind monatsunabhängig und landen auf der nächsten freien Rechnung des
   * Kunden; der Leistungsmonat lebt nur noch als Text in der Beschreibung.
   * Inklusive Kunden-Join, damit die UI/Übernahme den Unter-Kunden-Namen kennt.
   */
  getOpenForInvoice: async (invoice: Pick<Invoice, 'customer_id' | 'year' | 'month'>): Promise<Zusatzleistung[]> => {
    if (!invoice.customer_id) return [];

    const scopeIds = await zusatzleistungenService.getScopeCustomerIds(invoice.customer_id);

    const { data, error } = await supabase
      .from('zusatzleistungen')
      .select(`
        *,
        customer:customers(company, mandantennummer)
      `)
      .in('customer_id', scopeIds)
      .eq('status', 'offen')
      .order('created_at', { ascending: true })
      .limit(100000);

    if (error) {
      console.error('[ZusatzleistungenService] Fehler beim Laden offener Zusatzleistungen zur Rechnung:', error);
      return [];
    }
    return (data || []) as Zusatzleistung[];
  },

  /**
   * Ermittelt für eine Liste von Rechnungen (z.B. die Entwürfe der aktuellen
   * Ansicht) die Anzahl offener Zusatzleistungen je Rechnung – inklusive
   * Sammelrechnungs-Scope. Rückgabe: { [invoiceId]: anzahlOffen }.
   *
   * Effizient mit wenigen Queries + Merge in der Anwendung (keine N+1-Abfragen,
   * keine PostgREST-Embeds).
   */
  getOpenCountsForInvoices: async (invoices: Pick<Invoice, 'id' | 'customer_id' | 'year' | 'month'>[]): Promise<Record<string, number>> => {
    const result: Record<string, number> = {};
    for (const inv of invoices) result[inv.id] = 0;

    const draftCustomerIds = Array.from(
      new Set(invoices.map(i => i.customer_id).filter((v): v is string => !!v))
    );
    if (draftCustomerIds.length === 0) return result;

    // 1) Unter-Kunden je Auftraggeber laden
    const { data: subRows, error: subError } = await supabase
      .from('customers')
      .select('id, auftraggeber_id')
      .in('auftraggeber_id', draftCustomerIds)
      .eq('deleted', false)
      .limit(100000);

    if (subError) {
      console.error('[ZusatzleistungenService] Fehler beim Laden der Unter-Kunden (Batch):', subError);
    }

    const subsByAuftraggeber: Record<string, string[]> = {};
    for (const row of subRows || []) {
      if (!row.auftraggeber_id) continue;
      (subsByAuftraggeber[row.auftraggeber_id] ||= []).push(row.id);
    }

    // 2) Alle relevanten customer_ids (Empfänger + Unter-Kunden)
    const scopeAll = Array.from(new Set([
      ...draftCustomerIds,
      ...(subRows || []).map(r => r.id)
    ]));

    // 3) Offene Zusatzleistungen für diese Kunden laden (monatsunabhängig;
    //    nur die für die Zählung nötige Spalte)
    const { data: openRows, error: openError } = await supabase
      .from('zusatzleistungen')
      .select('customer_id')
      .eq('status', 'offen')
      .in('customer_id', scopeAll)
      .limit(100000);

    if (openError) {
      console.error('[ZusatzleistungenService] Fehler beim Laden offener Zusatzleistungen (Batch):', openError);
      return result;
    }

    // 4) Index customer_id -> Anzahl offener Zusatzleistungen (monatsunabhängig)
    const openIndex: Record<string, number> = {};
    for (const row of openRows || []) {
      openIndex[row.customer_id] = (openIndex[row.customer_id] || 0) + 1;
    }

    // 5) Pro Rechnung über deren Scope summieren
    for (const inv of invoices) {
      if (!inv.customer_id) continue;
      const scope = [inv.customer_id, ...(subsByAuftraggeber[inv.customer_id] || [])];
      let count = 0;
      for (const cid of scope) {
        count += openIndex[cid] || 0;
      }
      result[inv.id] = count;
    }

    return result;
  },

  // ============================================
  // ÜBERNAHME IN RECHNUNG
  // ============================================

  /**
   * Übernimmt eine offene Zusatzleistung als Position in einen Rechnungsentwurf.
   *
   * Ablauf:
   * 1. Zusatzleistung laden, Guard: Status 'offen'
   * 2. Rechnung laden, Guard: Status 'entwurf' (nur Entwürfe erhalten Positionen)
   * 3. Beschreibung bauen – bei Sammelrechnung (Zusatzleistung eines Unter-Kunden)
   *    mit Firmen-Präfix analog zur Lohn-Sammelposition
   * 4. Position anlegen; gesamtpreis = betrag der Zusatzleistung (verbindliche
   *    Betragsquelle, auch bei überschriebenem Betrag)
   * 5. Rechnungsbeträge neu berechnen (invoiceService.recalculateInvoiceTotals)
   * 6. Zusatzleistung auf 'in_rechnung' setzen und mit Rechnung/Position verknüpfen
   *
   * Kein Renumbering hier – die Position wird ans Ende gehängt; das finale
   * lückenlose Renumbering erfolgt ohnehin beim Festschreiben der Rechnung.
   */
  convertToInvoice: async (
    zusatzleistungId: string,
    invoiceId: string
  ): Promise<{ success: boolean; error?: string; lineItemId?: string }> => {
    try {
      // 1) Zusatzleistung laden
      const { data: zl, error: zlError } = await supabase
        .from('zusatzleistungen')
        .select('*')
        .eq('id', zusatzleistungId)
        .single();

      if (zlError || !zl) {
        return { success: false, error: 'Zusatzleistung nicht gefunden' };
      }
      if (zl.status !== 'offen') {
        return { success: false, error: 'Diese Zusatzleistung wurde bereits in eine Rechnung übernommen.' };
      }

      // 2) Rechnung laden + Guard
      const { data: invoice, error: invError } = await supabase
        .from('invoices')
        .select('id, status, customer_id')
        .eq('id', invoiceId)
        .single();

      if (invError || !invoice) {
        return { success: false, error: 'Rechnung nicht gefunden' };
      }
      if (invoice.status !== 'entwurf') {
        return { success: false, error: 'Zusatzleistungen können nur in Entwürfe übernommen werden. Diese Rechnung ist bereits freigegeben oder versendet.' };
      }

      // 3) Beschreibung bauen – bei Unter-Kunde (Sammelrechnung) Firmen-Präfix
      let beschreibung = zl.beschreibung as string;
      if (zl.customer_id !== invoice.customer_id) {
        const { data: subCustomer } = await supabase
          .from('customers')
          .select('company, company_rechnung')
          .eq('id', zl.customer_id)
          .maybeSingle();
        const firma = subCustomer?.company_rechnung || subCustomer?.company;
        if (firma) {
          beschreibung = `${firma} – ${beschreibung}`;
        }
      }

      // 4) Position-Werte ableiten. gesamtpreis = betrag (verbindliche Quelle).
      const betrag = Number(zl.betrag);
      let menge: number;
      let einheit: string;
      let einzelpreis: number;

      if (zl.art === 'stunden' && zl.stunden !== null && Number(zl.stunden) !== 0) {
        menge = Number(zl.stunden);
        einheit = 'Std';
        // Bevorzugt der gespeicherte Satz; ansonsten aus Betrag/Stunden abgeleitet.
        einzelpreis = zl.stundensatz !== null
          ? Number(zl.stundensatz)
          : Math.round((betrag / menge) * 100) / 100;
      } else {
        menge = 1;
        einheit = 'Pauschal';
        einzelpreis = betrag;
      }

      // Nächste Positionsnummer ermitteln (ans Ende hängen)
      const { data: existingItems } = await supabase
        .from('invoice_line_items')
        .select('position')
        .eq('invoice_id', invoiceId)
        .order('position', { ascending: false })
        .limit(1);

      const nextPosition = existingItems && existingItems.length > 0
        ? existingItems[0].position + 1
        : 1;

      // Position anlegen – gesamtpreis exakt = betrag
      const { data: lineItem, error: insertError } = await supabase
        .from('invoice_line_items')
        .insert({
          invoice_id: invoiceId,
          position: nextPosition,
          beschreibung,
          menge,
          einheit,
          einzelpreis,
          gesamtpreis: betrag
        })
        .select()
        .single();

      if (insertError || !lineItem) {
        console.error('[ZusatzleistungenService] Fehler beim Anlegen der Position:', insertError);
        return { success: false, error: insertError?.message || 'Position konnte nicht angelegt werden' };
      }

      // 5) Beträge neu berechnen (bestehende Logik)
      await invoiceService.recalculateInvoiceTotals(invoiceId);

      // 6) Zusatzleistung als übernommen markieren
      const { error: updateError } = await supabase
        .from('zusatzleistungen')
        .update({
          status: 'in_rechnung',
          invoice_id: invoiceId,
          invoice_line_item_id: lineItem.id,
          in_rechnung_am: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', zusatzleistungId)
        .eq('status', 'offen'); // Guard gegen Doppel-Übernahme (Race)

      if (updateError) {
        console.error('[ZusatzleistungenService] Fehler beim Markieren als in_rechnung:', updateError);
        // Position wurde bereits angelegt und Betrag neu berechnet. Wir melden
        // den Fehler, damit der Aufrufer den Zustand prüfen kann – kein stiller
        // Erfolg.
        return { success: false, error: 'Position wurde angelegt, aber der Status der Zusatzleistung konnte nicht aktualisiert werden. Bitte prüfen.' };
      }

      return { success: true, lineItemId: lineItem.id };
    } catch (error) {
      console.error('[ZusatzleistungenService] Unerwarteter Fehler bei der Übernahme:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  },

  /**
   * Übernimmt automatisch alle offenen Zusatzleistungen im Scope eines Entwurfs
   * – ABER NUR, wenn beim abrechnenden Kunden (Rechnungsempfänger; bei einer
   * Unterkunde ist das der Auftraggeber) ein gültiger Stundensatz hinterlegt ist.
   *
   * Ist kein Satz hinterlegt, wird bewusst nichts übernommen: Die Leistungen
   * bleiben offen und müssen manuell übernommen werden – so geht nichts still
   * verloren, und es landet nichts ungeprüft auf einer Rechnung.
   *
   * Nutzt intern convertToInvoice (inkl. Firmen-Präfix + Neuberechnung).
   */
  autoConvertOpenForInvoice: async (
    invoice: Pick<Invoice, 'id' | 'customer_id' | 'status' | 'year' | 'month'>
  ): Promise<{ convertedCount: number; skipped: boolean }> => {
    // Nur Entwürfe erhalten automatisch Positionen.
    if (invoice.status !== 'entwurf' || !invoice.customer_id) {
      return { convertedCount: 0, skipped: true };
    }

    // Bedingung: gültiger Stundensatz beim abrechnenden Kunden. getCurrentStundensatz
    // löst Unterkunden ohnehin auf den Auftraggeber auf; hier ist customer_id aber
    // bereits der Rechnungsempfänger.
    const satz = await zusatzleistungenService.getCurrentStundensatz(invoice.customer_id);
    if (!satz) {
      return { convertedCount: 0, skipped: true };
    }

    const open = await zusatzleistungenService.getOpenForInvoice({
      customer_id: invoice.customer_id, year: invoice.year, month: invoice.month
    });

    let convertedCount = 0;
    for (const zl of open) {
      const res = await zusatzleistungenService.convertToInvoice(zl.id, invoice.id);
      if (res.success) convertedCount++;
    }
    return { convertedCount, skipped: false };
  }
};
